import { denormalizeTransactions } from './denormalizeTransactions';

const INVESTMENT_KEYS = ['rrsp', 'tfsa', 'fhsa', 'resp', 'investment_account', 'investments'];

const ACCOUNT_META = {
  rrsp:               { label: 'RRSP',        color: '#6A55F7', roomField: 'contribution_room_remaining', roomLabel: 'Contribution room' },
  tfsa:               { label: 'TFSA',        color: '#7ec8a4', roomField: 'contribution_room_remaining', roomLabel: 'Contribution room' },
  fhsa:               { label: 'FHSA',        color: '#f4845f', roomField: 'contribution_room_remaining', roomLabel: 'Lifetime room left' },
  resp:               { label: 'RESP',        color: '#7aaee8', roomField: null, roomLabel: null },
  investment_account: { label: 'Investment',  color: '#e8c547', roomField: null, roomLabel: null },
  investments:        { label: 'Investments', color: '#e8c547', roomField: null, roomLabel: null },
};

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/**
 * Extracts investment account data from a profile object.
 * Returns null if no investment accounts are found.
 *
 * @param {object} profile - profile.accounts must exist
 * @returns {{ accounts, totalValue, chartData } | null}
 */
export function computeInvestmentData(profile) {
  if (!profile?.accounts) return null;

  const accounts = [];
  let totalValue = 0;
  const accountTxns = {}; // accountKey -> sorted flat transactions

  INVESTMENT_KEYS.forEach(key => {
    const acct = profile.accounts[key];
    if (!acct) return;

    const value = Number(
      acct.current_balance ?? acct.current_balance_owing ?? acct.current_value ?? acct.current_market_value ?? acct.balance ?? 0
    );
    if (!value) return;

    const meta = ACCOUNT_META[key] ?? { label: key, color: 'var(--td-green)', roomField: null, roomLabel: null };
    const room = meta.roomField != null ? acct[meta.roomField] : null;

    totalValue += value;

    const txns = denormalizeTransactions(acct.transactions)
      .filter(t => t.date && t.balance_after != null)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    accountTxns[key] = txns;

    accounts.push({
      id:             key,
      label:          meta.label,
      color:          meta.color,
      value,
      room:           room != null ? Number(room) : null,
      roomLabel:      meta.roomLabel,
      strategy:       acct.strategy || acct.investment_type || null,
      holdingsSummary: acct.holdings_summary || null,
      accountType:    acct.account_type || null,
      // Extract explicit ticker holdings if present
      holdings:       extractHoldings(acct),
    });
  });

  if (!accounts.length) return null;

  const chartData = buildPortfolioChart(accounts, accountTxns);
  return { accounts, totalValue, chartData };
}

/** Pull tickers from an account if they're stored as a holdings array */
function extractHoldings(acct) {
  if (Array.isArray(acct.holdings)) return acct.holdings;
  if (Array.isArray(acct.positions)) return acct.positions;
  return [];
}

/**
 * Builds a monthly portfolio value timeline from sparse transaction histories.
 * For each month, the last known balance per account is carried forward.
 */
function buildPortfolioChart(accounts, accountTxns) {
  // Collect all unique YYYY-MM months across all transaction sets
  const allMonths = new Set();
  Object.values(accountTxns).forEach(txns =>
    txns.forEach(t => allMonths.add(t.date.slice(0, 7)))
  );

  if (!allMonths.size) return [];

  const sortedMonths = [...allMonths].sort();
  const [firstYr, firstMo] = sortedMonths[0].split('-').map(Number);
  const [lastYr,  lastMo]  = sortedMonths[sortedMonths.length - 1].split('-').map(Number);

  // Expand to a continuous month range (fill any gaps)
  const months = [];
  let yr = firstYr, mo = firstMo;
  while (yr < lastYr || (yr === lastYr && mo <= lastMo)) {
    months.push(`${yr}-${String(mo).padStart(2, '0')}`);
    mo++;
    if (mo > 12) { mo = 1; yr++; }
  }

  // For each account, map month -> last balance_after in that month
  const acctMonthBal = {};
  Object.entries(accountTxns).forEach(([key, txns]) => {
    const byMonth = {};
    txns.forEach(t => { byMonth[t.date.slice(0, 7)] = t.balance_after; });
    acctMonthBal[key] = byMonth;
  });

  // Walk through months, carrying forward last known balance per account
  const lastKnown = {};
  accounts.forEach(a => { lastKnown[a.id] = null; });

  return months.map(month => {
    Object.keys(acctMonthBal).forEach(key => {
      if (acctMonthBal[key][month] !== undefined) {
        lastKnown[key] = acctMonthBal[key][month];
      }
    });

    const total = Object.values(lastKnown).reduce((s, v) => s + (v ?? 0), 0);
    const [, mo] = month.split('-');
    const label = MONTH_LABELS[parseInt(mo, 10) - 1];

    return { month, label, value: Math.round(total) };
  }).filter(d => d.value > 0); // skip leading zeros before first txns appear
}
